import {
  type AbstractControl,
  type ValidationErrors,
  Validators,
} from '@angular/forms';
import { type ErrorStateMatcher } from '@angular/material/core';
import {
  type TypedFormArray,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  type INzStafferPracticeProviderDetails,
  type IPractice,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  isSameRef,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';

const practiceRequiredError = 'practiceRequired';

export class NzPracticeProviderDetailsFormGroup extends TypedFormGroup<INzStafferPracticeProviderDetails> {
  constructor() {
    super(
      {
        practiceRef: new TypedFormControl<DocumentReference<IPractice>>(),
        payeeNumber: new TypedFormControl<string>('', Validators.maxLength(6)),
        agreementNumber: new TypedFormControl<string>(
          '',
          Validators.maxLength(6)
        ),
      },
      NzPracticeProviderDetailsFormGroup._requirePracticeForNumbers
    );
  }

  private static _requirePracticeForNumbers(
    group: AbstractControl
  ): ValidationErrors | null {
    const { practiceRef, payeeNumber, agreementNumber } =
      group.value as Partial<INzStafferPracticeProviderDetails>;
    const hasNumbers = !!payeeNumber || !!agreementNumber;
    if (hasNumbers && !practiceRef) {
      return { [practiceRequiredError]: true };
    }
    // eslint-disable-next-line no-null/no-null
    return null;
  }
}

/**
 * Flags the practice select when its row has numbers but no practice, since
 * the error lives on the row group rather than the practice control.
 */
export class NzPracticeRequiredErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null): boolean {
    return !!control?.parent?.hasError(practiceRequiredError);
  }
}

export class NzPracticeProviderDetailsControls {
  constructor(
    private _formArray: TypedFormArray<INzStafferPracticeProviderDetails>
  ) {}

  static selectablePracticesByRow(
    practices: WithRef<IPractice>[],
    rows: INzStafferPracticeProviderDetails[]
  ): WithRef<IPractice>[][] {
    return rows.map((_row, rowIndex) =>
      practices.filter(
        (practice) =>
          !rows.some(
            (otherRow, otherIndex) =>
              otherIndex !== rowIndex &&
              isSameRef(otherRow.practiceRef, practice)
          )
      )
    );
  }

  addControl(index: number, options?: { emitEvent?: boolean }): void {
    this._formArray.insert(
      index + 1,
      new NzPracticeProviderDetailsFormGroup(),
      options
    );
  }

  removeControl(index: number): void {
    if (index < 1) {
      return;
    }
    this._formArray.removeAt(index);
  }

  reset(options?: { emitEvent?: boolean }): void {
    this._formArray.clear(options);
    this.addControl(0, options);
  }

  initNzPracticeProviderDetailsControls(
    staffer: IStaffer,
    options?: { emitEvent?: boolean }
  ): void {
    this.reset(options);
    const details = staffer.metadata?.nz?.practiceProviderDetails ?? [];
    details.map((_, index) =>
      index ? this.addControl(index, options) : undefined
    );
  }
}
